import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Plus, Edit, Trash2, Save, X, LogOut, Bold, Heading, List } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const emptyBlog = {
    slug: '', title: '', excerpt: '', content: '',
    date: new Date().toISOString().split('T')[0],
    author: 'Paix Family Mediation', image: '',
    hasComparisonTable: false, published: true
  };

  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      fetch(`${API_URL}/api/admin/verify?token=${savedToken}`)
        .then(res => res.json())
        .then(data => {
          if (data.valid) {
            setToken(savedToken);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('adminToken');
          }
        })
        .catch(() => localStorage.removeItem('adminToken'));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${API_URL}/api/blogs?published_only=false`)
        .then(res => res.json())
        .then(data => setBlogs(data.blogs || []))
        .catch(err => console.error(err));
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok) {
        setToken(data.token); setIsAuthenticated(true);
        localStorage.setItem('adminToken', data.token);
      } else setError(data.detail || 'Invalid password');
    } catch (err) { setError('Connection error'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(''); setIsAuthenticated(false); setBlogs([]);
  };

  const handleSaveBlog = async () => {
    setLoading(true); setError('');
    try {
      const url = isCreating 
        ? `${API_URL}/api/blogs?token=${token}`
        : `${API_URL}/api/blogs/${editingBlog.originalSlug || editingBlog.slug}?token=${token}`;
      const res = await fetch(url, {
        method: isCreating ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingBlog)
      });
      if (res.ok) {
        const blogsRes = await fetch(`${API_URL}/api/blogs?published_only=false`);
        const blogsData = await blogsRes.json();
        setBlogs(blogsData.blogs || []);
        setEditingBlog(null); setIsCreating(false);
      } else { const data = await res.json(); setError(data.detail || 'Error saving'); }
    } catch (err) { setError('Connection error'); }
    finally { setLoading(false); }
  };

  const handleDeleteBlog = async (slug) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const res = await fetch(`${API_URL}/api/blogs/${slug}?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        const blogsRes = await fetch(`${API_URL}/api/blogs?published_only=false`);
        const blogsData = await blogsRes.json();
        setBlogs(blogsData.blogs || []);
      }
    } catch (err) { setError('Connection error'); }
  };

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const insertFormatting = (format) => {
    const textarea = document.getElementById('content-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editingBlog.content;
    const selectedText = text.substring(start, end);
    
    let newText = '';
    let cursorOffset = 0;
    
    switch(format) {
      case 'heading':
        newText = text.substring(0, start) + '\n## ' + selectedText + text.substring(end);
        cursorOffset = 4;
        break;
      case 'bold':
        newText = text.substring(0, start) + '**' + selectedText + '**' + text.substring(end);
        cursorOffset = 2;
        break;
      case 'list':
        newText = text.substring(0, start) + '\n- ' + selectedText + text.substring(end);
        cursorOffset = 3;
        break;
      default:
        return;
    }
    
    setEditingBlog({ ...editingBlog, content: newText });
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, end + cursorOffset);
    }, 0);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-center mb-2" style={{ color: '#5E7F8A' }}>Blog Admin</h1>
          <p className="text-gray-600 text-center mb-6">Enter password to continue</p>
          <form onSubmit={handleLogin}>
            <div className="relative mb-4">
              <input type={showPassword ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                className="w-full px-4 py-3 border rounded-lg" style={{ borderColor: '#9BB2A2' }} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-lg text-white font-semibold" style={{ backgroundColor: '#9BB2A2' }}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (editingBlog) {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ color: '#5E7F8A' }}>
              {isCreating ? 'Create New Post' : 'Edit Post'}
            </h2>
            <button onClick={() => { setEditingBlog(null); setIsCreating(false); }} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          {error && <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4">{error}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#5E7F8A' }}>Title</label>
              <input type="text" value={editingBlog.title}
                onChange={(e) => setEditingBlog({ ...editingBlog, title: e.target.value, slug: isCreating ? generateSlug(e.target.value) : editingBlog.slug })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50" 
                style={{ focusRing: '#9BB2A2' }}
                placeholder="Blog post title" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#5E7F8A' }}>Slug (URL)</label>
              <input type="text" value={editingBlog.slug}
                onChange={(e) => setEditingBlog({ ...editingBlog, slug: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#5E7F8A' }}>Date</label>
              <input type="date" value={editingBlog.date}
                onChange={(e) => setEditingBlog({ ...editingBlog, date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#5E7F8A' }}>Excerpt (Preview text for blog list)</label>
              <textarea value={editingBlog.excerpt}
                onChange={(e) => setEditingBlog({ ...editingBlog, excerpt: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg" rows={2} 
                placeholder="Brief description shown on blog list page" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#5E7F8A' }}>Image URL</label>
              <input type="text" value={editingBlog.image}
                onChange={(e) => setEditingBlog({ ...editingBlog, image: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg" placeholder="https://images.unsplash.com/..." />
              {editingBlog.image && <img src={editingBlog.image} alt="Preview" className="mt-2 h-32 object-cover rounded" />}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#5E7F8A' }}>Content</label>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => insertFormatting('heading')}
                  className="flex items-center gap-1 px-3 py-1 text-sm border rounded hover:bg-gray-100" title="Add Heading">
                  <Heading size={16} /> Heading
                </button>
                <button type="button" onClick={() => insertFormatting('bold')}
                  className="flex items-center gap-1 px-3 py-1 text-sm border rounded hover:bg-gray-100" title="Bold">
                  <Bold size={16} /> Bold
                </button>
                <button type="button" onClick={() => insertFormatting('list')}
                  className="flex items-center gap-1 px-3 py-1 text-sm border rounded hover:bg-gray-100" title="Bullet List">
                  <List size={16} /> List
                </button>
              </div>
              <textarea 
                id="content-editor"
                value={editingBlog.content}
                onChange={(e) => setEditingBlog({ ...editingBlog, content: e.target.value })}
                className="w-full px-4 py-3 border rounded-lg font-mono text-sm" 
                rows={15}
                placeholder="Write your blog content here. Use the buttons above to add formatting, or type directly." />
              <p className="text-xs text-gray-500 mt-1">
                Tip: Use ## for headings, **text** for bold, and - for bullet points
              </p>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editingBlog.published}
                  onChange={(e) => setEditingBlog({ ...editingBlog, published: e.target.checked })} 
                  className="w-4 h-4" />
                <span className="text-sm" style={{ color: '#5E7F8A' }}>Published</span>
              </label>
            </div>
            <div className="flex gap-4 pt-4">
              <button onClick={handleSaveBlog} disabled={loading}
                className="flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold"
                style={{ backgroundColor: '#9BB2A2' }}>
                <Save size={20} /> {loading ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setEditingBlog(null); setIsCreating(false); }}
                className="px-6 py-3 rounded-lg border font-semibold" style={{ borderColor: '#5E7F8A', color: '#5E7F8A' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#5E7F8A' }}>Blog Management</h1>
          <div className="flex gap-4">
            <button onClick={() => { setEditingBlog(emptyBlog); setIsCreating(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold"
              style={{ backgroundColor: '#9BB2A2' }}>
              <Plus size={20} /> New Post
            </button>
            <button onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border font-semibold"
              style={{ borderColor: '#5E7F8A', color: '#5E7F8A' }}>
              <LogOut size={20} /> Logout
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#5E7F8A' }}>
                <th className="px-6 py-4 text-left text-white font-semibold">Title</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Status</th>
                <th className="px-6 py-4 text-right text-white font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-500">No posts yet. Click "New Post" to create your first blog.</td></tr>
              ) : blogs.map((blog, i) => (
                <tr key={blog.slug} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-6 py-4">
                    <div className="font-medium" style={{ color: '#5E7F8A' }}>{blog.title}</div>
                    <div className="text-sm text-gray-500">/{blog.slug}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{blog.date}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${blog.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {blog.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => setEditingBlog({ ...blog, originalSlug: blog.slug })} className="text-blue-600 mr-4 hover:text-blue-800"><Edit size={18} /></button>
                    <button onClick={() => handleDeleteBlog(blog.slug)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
